/**
 * Color utility functions based on the LeanAI logo color scheme
 * Colors match the spectrum in the logo: Green → Teal → Light Blue → Blue → Purple
 */

export interface ScoreColors {
  background: string;
  text: string;
  border: string;
  borderLeft: string;
  textOnly: string;
}

/**
 * Get color scheme for priority/score values (0-10 scale)
 * Based on logo colors: Green (excellent) → Purple (poor)
 */
export const getScoreColors = (score: number, isEffortScore: boolean = false): ScoreColors => {
  // For effort scores, invert the logic (higher effort = worse = purple)
  const effectiveScore = isEffortScore ? 10 - score : score;
  
  if (effectiveScore >= 8) {
    // Excellent - Vibrant Green (matches logo)
    return {
      background: 'bg-green-500',
      text: 'text-white',
      border: 'border-green-500',
      borderLeft: 'border-l-green-500',
      textOnly: 'text-green-500 font-bold'
    };
  } else if (effectiveScore >= 7) {
    // Very Good - Vibrant Teal (matches logo)
    return {
      background: 'bg-teal-500',
      text: 'text-white',
      border: 'border-teal-500',
      borderLeft: 'border-l-teal-500',
      textOnly: 'text-teal-500 font-bold'
    };
  } else if (effectiveScore >= 6) {
    // Good - Vibrant Cyan (matches logo)
    return {
      background: 'bg-cyan-500',
      text: 'text-white',
      border: 'border-cyan-500',
      borderLeft: 'border-l-cyan-500',
      textOnly: 'text-cyan-500 font-bold'
    };
  } else if (effectiveScore >= 5) {
    // Fair - Vibrant Blue (matches logo)
    return {
      background: 'bg-blue-500',
      text: 'text-white',
      border: 'border-blue-500',
      borderLeft: 'border-l-blue-500',
      textOnly: 'text-blue-500 font-bold'
    };
  } else if (effectiveScore >= 4) {
    // Poor - Vibrant Purple (matches logo)
    return {
      background: 'bg-purple-500',
      text: 'text-white',
      border: 'border-purple-500',
      borderLeft: 'border-l-purple-500',
      textOnly: 'text-purple-500 font-bold'
    };
  } else {
    // Very Poor - Vibrant Dark Purple
    return {
      background: 'bg-violet-600',
      text: 'text-white',
      border: 'border-violet-600',
      borderLeft: 'border-l-violet-600',
      textOnly: 'text-violet-600 font-bold'
    };
  }
};

/**
 * Get hex colors for charts/visualizations (matching logo spectrum)
 */
export const getScoreHexColor = (score: number, isEffortScore: boolean = false): string => {
  const effectiveScore = isEffortScore ? 10 - score : score;
  
  if (effectiveScore >= 8) return '#22c55e'; // green-500
  if (effectiveScore >= 7) return '#14b8a6'; // teal-500  
  if (effectiveScore >= 6) return '#06b6d4'; // cyan-500
  if (effectiveScore >= 5) return '#3b82f6'; // blue-500
  if (effectiveScore >= 4) return '#8b5cf6'; // purple-500
  return '#7c3aed'; // violet-600
};

/**
 * Get color scale for Vega-Lite charts - dynamically generated from core colors
 */
export const getChartColorScale = () => {
  return {
    domain: [0, 4, 5, 6, 7, 8, 10],
    range: [
      '#7c3aed',   // violet-600 - Very Poor (more saturated)
      '#8b5cf6',   // purple-500 - Poor  
      '#3b82f6',   // blue-500 - Fair
      '#06b6d4',   // cyan-500 - Good
      '#14b8a6',   // teal-500 - Very Good
      '#22c55e',   // green-500 - Excellent
      '#16a34a'    // green-600 - Maximum excellence (even more saturated)
    ]
  };
};

/**
 * Legacy function names for backward compatibility
 */
export const getPriorityColor = (score: number): string => {
  const colors = getScoreColors(score);
  return `${colors.background} ${colors.text}`;
};

export const getPriorityBorderColor = (score: number): string => {
  const colors = getScoreColors(score);
  return `border-l-${colors.border.replace('border-', '')}`;
};

export const getScoreColor = (score: number, maxScore: number = 10, isEffort: boolean = false): string => {
  const normalizedScore = (score / maxScore) * 10;
  const colors = getScoreColors(normalizedScore, isEffort);
  return colors.textOnly;
};

/**
 * Get colors for project status (non-score based)
 */
export const getStatusColors = (status: string): ScoreColors => {
  switch (status.toLowerCase()) {
    case 'active':
      return {
        background: 'bg-green-500',
        text: 'text-white',
        border: 'border-green-500',
        borderLeft: 'border-l-green-500',
        textOnly: 'text-green-500'
      };
    case 'completed':
      return {
        background: 'bg-blue-500',
        text: 'text-white',
        border: 'border-blue-500',
        borderLeft: 'border-l-blue-500',
        textOnly: 'text-blue-500'
      };
    case 'on-hold':
      return {
        background: 'bg-purple-500',
        text: 'text-white',
        border: 'border-purple-500',
        borderLeft: 'border-l-purple-500',
        textOnly: 'text-purple-500'
      };
    case 'archived':
    default:
      return {
        background: 'bg-gray-500',
        text: 'text-white',
        border: 'border-gray-500',
        borderLeft: 'border-l-gray-500',
        textOnly: 'text-gray-500'
      };
  }
};